/**
 * useBookCapacity — shared read of GET /api/professional/book-capacity for
 * the book-capacity upgrade surfaces (BookCapacityBanner + the PeoplePage
 * roster meter).
 *
 * Module-level cache: both surfaces mount on the same pages, so the first
 * mount fetches and every other mount (and StrictMode's double-invoke) reuses
 * the in-flight promise / cached value — dedupe keyed on "loaded", not
 * "render started".
 *
 * FAIL QUIET: loading, fetch error, and `max_clients: -1` (unlimited) all
 * resolve to `null` — callers render nothing. A missing banner beats a wrong
 * one ("hidden beats wrong", same contract as the backend read).
 *
 * IS_PREVIEW: never fetches. `?bookcap=<count>/<cap>` (e.g. `?bookcap=26/25`)
 * forces a fixture so preview routes and e2e specs can drive each visual
 * state; absent param renders nothing (no pollution of unrelated preview
 * screenshots). Mirrors the `?billing=` / `?copilotDemo=` seam idiom.
 */
import { useEffect, useState } from "react";
import { type BookCapacityView, getBookCapacity } from "../lib/api";
import { IS_PREVIEW, previewQueryParam } from "../lib/pro-mode";
import { useProToken } from "./useProToken";

let cached: BookCapacityView | null = null;
let loaded = false;
let inflight: Promise<BookCapacityView | null> | null = null;

/** Test-only: reset the module cache between vitest cases. */
export function resetBookCapacityCacheForTest(): void {
  cached = null;
  loaded = false;
  inflight = null;
}

function previewFixture(): BookCapacityView | null {
  const raw = previewQueryParam("bookcap");
  if (!raw) return null;
  const match = /^(\d+)\/(\d+)$/.exec(raw);
  if (!match) return null;
  return {
    agent_added_count: Number(match[1]),
    max_clients: Number(match[2]),
  };
}

/**
 * Returns the capacity view once loaded, or `null` while loading / on error /
 * on an unlimited plan. Callers treat `null` as "render nothing".
 */
export function useBookCapacity(): BookCapacityView | null {
  const getToken = useProToken();
  const [view, setView] = useState<BookCapacityView | null>(() =>
    IS_PREVIEW ? previewFixture() : loaded ? cached : null,
  );

  useEffect(() => {
    // Already loaded → the useState initializer read the cache; nothing to do.
    if (IS_PREVIEW || loaded) return;
    let cancelled = false;
    inflight ??= (async () => {
      try {
        const token = await getToken();
        const result = await getBookCapacity(token ?? undefined);
        cached = result.max_clients === -1 ? null : result;
      } catch {
        cached = null;
      }
      loaded = true;
      return cached;
    })();
    void inflight.then((result) => {
      if (!cancelled) setView(result);
    });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  return view && view.max_clients !== -1 ? view : null;
}
